import { useSetAtom, useStore } from "jotai";
import { useEffect, useLayoutEffect, useRef } from "react";

import {
  authenticatedLanguagePreferenceAtom,
  normalizeLanguage,
  storedLanguagePreferenceAtom,
} from "./atoms";
import { useUpdateLanguagePreference } from "./useUpdateLanguagePreference";

type UseSyncAuthenticatedLanguagePreferenceOptions = {
  active: boolean;
  preferredLocale?: string | null;
  userId?: string;
};

export const useSyncAuthenticatedLanguagePreference = ({
  active,
  preferredLocale,
  userId,
}: UseSyncAuthenticatedLanguagePreferenceOptions) => {
  const store = useStore();
  const setAuthenticatedLanguagePreference = useSetAtom(
    authenticatedLanguagePreferenceAtom
  );
  const setStoredLanguagePreference = useSetAtom(storedLanguagePreferenceAtom);
  const [updateLanguagePreference] = useUpdateLanguagePreference();
  const lastSyncAttemptRef = useRef<string | null>(null);
  const authenticatedLanguage = normalizeLanguage(preferredLocale);

  useLayoutEffect(() => {
    if (!active || !userId) {
      lastSyncAttemptRef.current = null;
      setAuthenticatedLanguagePreference(null);
      return;
    }

    setAuthenticatedLanguagePreference(authenticatedLanguage);

    if (authenticatedLanguage) {
      lastSyncAttemptRef.current = null;
      setStoredLanguagePreference(authenticatedLanguage);
    }
  }, [
    active,
    authenticatedLanguage,
    setAuthenticatedLanguagePreference,
    setStoredLanguagePreference,
    userId,
  ]);

  useEffect(() => {
    if (!active || !userId || authenticatedLanguage) {
      return;
    }

    const storedLanguage = store.get(storedLanguagePreferenceAtom);

    if (!storedLanguage) {
      return;
    }

    const syncKey = `${userId}:${storedLanguage}`;

    if (lastSyncAttemptRef.current === syncKey) {
      return;
    }

    lastSyncAttemptRef.current = syncKey;

    const syncStoredLanguage = (languageToSync: "en" | "fr"): void => {
      updateLanguagePreference({
        variables: { input: { preferredLocale: languageToSync } },
        onCompleted: (response) => {
          const latestStoredLanguage = store.get(storedLanguagePreferenceAtom);

          if (latestStoredLanguage && latestStoredLanguage !== languageToSync) {
            lastSyncAttemptRef.current = `${userId}:${latestStoredLanguage}`;
            syncStoredLanguage(latestStoredLanguage);
            return;
          }

          const persistedLanguage = normalizeLanguage(
            response.responsePayload?.user?.preferredLocale
          );

          if (persistedLanguage && latestStoredLanguage === languageToSync) {
            setAuthenticatedLanguagePreference(persistedLanguage);
            void setStoredLanguagePreference(persistedLanguage);
          }
        },
      });
    };

    syncStoredLanguage(storedLanguage);
  }, [
    active,
    authenticatedLanguage,
    setAuthenticatedLanguagePreference,
    setStoredLanguagePreference,
    store,
    updateLanguagePreference,
    userId,
  ]);

  useLayoutEffect(
    () => () => {
      setAuthenticatedLanguagePreference(null);
    },
    [setAuthenticatedLanguagePreference]
  );
};
